import { useCallback } from "react";
import Point from "ol/geom/Point";
import { DrawEvent } from "ol/interaction/Draw";
import GetLocation from "@avinet/adaptive-ui-maps/control/GetLocation";
import Draw from "@avinet/adaptive-ui-maps/vector/interaction/Draw";
import VectorSource from "@avinet/adaptive-ui-maps/vector/source/VectorSource";
import defaultStyle from "@avinet/adaptive-ui-maps/vector/style/defaultStyle";
import VectorLayer from "@avinet/adaptive-ui-maps/vector/VectorLayer";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import useGeometryPicker from "../../hooks/useGeometryPicker";
import { Icon } from "../Icon";
import "./PointGeometryPicker.scss";

const style = defaultStyle({
  pointRadius: 7,
  fillColor: "#006699",
  strokeColor: "#FFF",
  strokeWidth: 2,
});

interface PointGeometryPickerProps {
  value: string | null;
  onValueChange: (wkt: string | null) => void;
  onValidityChange: (isValid: boolean) => void;
  autoZoom?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  mapSrid?: string;
  srid?: string;
}

const PointGeometryPicker = ({
  value,
  onValueChange,
  autoZoom,
  readOnly,
  disabled,
  mapSrid,
  srid,
}: PointGeometryPickerProps) => {
  const t = useTranslate("PointGeometryPicker");
  const { feature, onLocation, onChange } = useGeometryPicker<Point>({
    value,
    onValueChange,
    autoZoom,
    useLocationAsValue: true,
    mapSrid,
    srid,
  });

  const onDrawEnd = useCallback(
    (e: DrawEvent) => {
      onChange(e.feature);
    },
    [feature, onChange]
  );

  return (
    <div className="geometry-picker geometry-picker-point">
      <GetLocation onLocation={onLocation} title={t("location")} />
      <div className="edit-buttons">
        {feature && (
          <button
            type="button"
            className="btn map btn-reset warning"
            onClick={() => onChange(null)}
          >
            <Icon name="delete" /> {t("reset")}
          </button>
        )}
      </div>
      <VectorLayer style={style} zIndex={1}>
        <VectorSource features={(feature && [feature]) || []} />
        {!readOnly && !disabled && <Draw type="Point" onDrawEnd={onDrawEnd} />}
      </VectorLayer>
    </div>
  );
};

export default PointGeometryPicker;
